import { api } from '../core';

export type MedicalMatchQuestionStatus = 'NEW' | 'PROCESSING' | 'ANSWERED' | 'FAILED';

export type MedicalMatchQuestionAskedWhen = 'CASE_CREATION' | 'LIVE';

export interface MedicalMatchQuestion {
  questionId: string;
  question: string;
  askedWhen: MedicalMatchQuestionAskedWhen;
  lastAnsweredAt: string | null;
  status: MedicalMatchQuestionStatus | null;
  /** Entries this question produced; empty until it has been answered. */
  medicalMatchEntryIds: string[];
}

export interface ListMedicalMatchQuestionsResponse {
  questions: MedicalMatchQuestion[];
}

export interface ListMedicalMatchQuestionsRequest {
  caseId: string;
  askedWhen?: MedicalMatchQuestionAskedWhen[];
  status?: MedicalMatchQuestionStatus[];
  /** Upper bound on age: only questions whose row changed within this window. */
  maxTimeSinceUpdatedSeconds?: number;
}

// The backend takes list filters as one comma-separated param rather than a
// repeated key, e.g. `?status=ANSWERED,FAILED`.
const buildQuery = ({ caseId, askedWhen, status, maxTimeSinceUpdatedSeconds }: ListMedicalMatchQuestionsRequest) => {
  const params = new URLSearchParams({ caseId });
  if (askedWhen?.length) params.set('askedWhen', askedWhen.join(','));
  if (status?.length) params.set('status', status.join(','));
  if (maxTimeSinceUpdatedSeconds !== undefined) {
    params.set('maxTimeSinceUpdatedSeconds', String(maxTimeSinceUpdatedSeconds));
  }
  return params.toString();
};

const listMedicalMatchQuestionsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listMedicalMatchQuestions: builder.query<ListMedicalMatchQuestionsResponse, ListMedicalMatchQuestionsRequest>({
      query: (request: ListMedicalMatchQuestionsRequest) => ({
        url: `medical-match-custom/list?${buildQuery(request)}`,
        method: 'GET',
      }),
      // No tags: each lookup is a one-off read of what settled in a window, so a
      // send or rerun refetching the last ones asked would only repeat a stale
      // question. The filters are time-relative for the same reason, so a
      // cached answer from a minute ago is a different question to the one
      // being asked now.
      keepUnusedDataFor: 0,
      extraOptions: { maxRetries: 3 },
    }),
  }),
});

export const { useLazyListMedicalMatchQuestionsQuery } = listMedicalMatchQuestionsApi;
